/**
 * Persistence for `MediaPreferences`.
 *
 * Parsed with Zod, never trusted: a half-written or hand-edited localStorage
 * entry must degrade to defaults rather than throw inside a render. Unknown
 * device ids are kept as-is — `enumerateDevices` decides whether they still
 * exist, not this module.
 */
import { z } from 'zod';
import { DEFAULT_MEDIA_PREFERENCES, type MediaPreferences } from './preferences';

const STORAGE_KEY = 'cyphex.teams.media.v1';

const audioSchema = z.object({
  noiseSuppression: z.enum(['off', 'native', 'rnnoise']),
  echoCancellation: z.boolean(),
  autoGainControl: z.boolean(),
});

const videoSchema = z.object({
  kind: z.enum(['none', 'blur', 'image']),
  blurStrength: z.enum(['light', 'strong']),
  backgroundId: z.string().max(120).nullable(),
});

const devicesSchema = z.object({
  cameraId: z.string().max(300).nullable(),
  micId: z.string().max(300).nullable(),
  speakerId: z.string().max(300).nullable(),
});

const preferencesSchema = z.object({
  audio: audioSchema,
  video: videoSchema,
  devices: devicesSchema,
  lowBandwidth: z.boolean(),
});

/** Reads the stored preferences, falling back to defaults on anything odd. */
export function loadMediaPreferences(): MediaPreferences {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_MEDIA_PREFERENCES;
    const parsed = preferencesSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_MEDIA_PREFERENCES;
  } catch {
    return DEFAULT_MEDIA_PREFERENCES;
  }
}

/** Best-effort write. A full quota must never break a meeting control. */
export function saveMediaPreferences(preferences: MediaPreferences): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    /* Private mode or a full quota — the session still works, unpersisted. */
  }
}
